import { EventType } from '@graphql/generated-base';

/**
 * Returns `true` for events whose schedule data lives in the legacy Firebase/wedding backend
 * rather than the Postgres-backed pro pipeline.
 *
 * Used to branch schedule queries, mutations, and cache strategies.
 *
 * Event type alone is not enough to answer this. A wedding created on the new stack has no Firebase
 * document, so the legacy backend has nothing to serve it and nothing to write to — the admin save
 * fails on the legacy authorization lookup before it reaches Firebase, and the label mapping comes
 * back empty. Those events belong on the Postgres path, which `event_service` already serves for
 * them.
 *
 * Pass `isNewStackEvent` from `useIsNewStackEvent()` so every schedule surface agrees.
 */
export const usesFirebaseScheduleBackend = (eventType: EventType | null | undefined, isNewStackEvent = false): boolean => {
  if (isNewStackEvent) {
    return false;
  }

  return eventType === EventType.wedding || eventType === EventType.babyRegistry;
};

/**
 * Does this event's schedule come back from the general-event query,
 * `getGuestGeneralEventScheduleForEvent`?
 *
 * This is **not** the complement of `usesFirebaseScheduleBackend`. That one answers a question
 * about backends and returns `false` for a card event and for the window before the event type has
 * resolved — and the general-event resolver accepts neither: it throws for anything that is not a
 * general event or a new-stack event. So there is a third state, "neither query", and any caller
 * choosing a query has to ask this predicate rather than negating the other one.
 *
 * A new-stack wedding belongs here: it has no Firebase document, so `event_service` serves its
 * schedule from Postgres exactly as it does a general event's, and the resolver admits it on
 * `usesNewStack` for that reason.
 */
export const usesGeneralEventScheduleQuery = (eventType: EventType | null | undefined, isNewStackEvent = false): boolean => {
  // Type not resolved yet: the wedding-shaped query is the one that mounts.
  if (eventType == null) return false;

  return eventType === EventType.general || isNewStackEvent;
};
